import { useEffect, useMemo } from "react";

import { SUPPORTED_RESOLUTIONS_V2 } from "config/tradingview";
import { selectChartToken } from "context/SyntheticsStateContext/selectors/chartSelectors";
import { selectChartLines } from "context/SyntheticsStateContext/selectors/chartSelectors/selectChartLines";
import { selectSetIsCandlesLoaded } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { selectSelectedMarketVisualMultiplier } from "context/SyntheticsStateContext/selectors/statsSelectors";
import { selectTradeboxMarketAddress } from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { shouldInvertChartPrice } from "domain/prices/chartInversion";
import { useLatestCandleClose } from "domain/prices/useLatestCandleClose";
import { useChainId } from "lib/chains";
import { CHART_PERIODS } from "lib/legacy";
import { useLocalStorageSerializeKey } from "lib/localStorage";

import TVChartContainer from "components/TVChartContainer/TVChartContainer";

import "./TVChart.scss";

const DEFAULT_PERIOD = "5m";

export function TVChart() {
  const { chainId } = useChainId();
  const { chartToken, symbol: chartTokenSymbol } = useSelector(selectChartToken);
  const visualMultiplier = useSelector(selectSelectedMarketVisualMultiplier);
  const marketAddress = useSelector(selectTradeboxMarketAddress);
  const setIsCandlesLoaded = useSelector(selectSetIsCandlesLoaded);

  let [period, setPeriod] = useLocalStorageSerializeKey([chainId, "Chart-period-v2"], DEFAULT_PERIOD);

  if (!period || !(period in CHART_PERIODS)) {
    period = DEFAULT_PERIOD;
  }

  const chartLines = useSelector(selectChartLines);

  const latestCandleClose = useLatestCandleClose(chainId, chartTokenSymbol, period);

  useEffect(
    function updatePeriod() {
      if (!period || !(period in CHART_PERIODS)) {
        setPeriod(DEFAULT_PERIOD);
      }
    },
    [period, setPeriod]
  );

  const chartTokenProp = useMemo(
    () =>
      chartToken
        ? {
            symbol: chartToken.symbol,
            ...(latestCandleClose ? { minPrice: latestCandleClose, maxPrice: latestCandleClose } : chartToken.prices),
          }
        : {
            symbol: chartTokenSymbol || "",
            minPrice: 0n,
            maxPrice: 0n,
          },
    [chartToken, chartTokenSymbol, latestCandleClose]
  );

  const chartTokenAvgPrice =
    "minPrice" in chartTokenProp &&
    "maxPrice" in chartTokenProp &&
    chartTokenProp.minPrice !== undefined &&
    chartTokenProp.maxPrice !== undefined
      ? (chartTokenProp.minPrice + chartTokenProp.maxPrice) / 2n
      : undefined;
  const shouldInvertChartLines = shouldInvertChartPrice(chartTokenAvgPrice);

  const displayedChartLines = useMemo(
    () =>
      shouldInvertChartLines
        ? chartLines.map((line) => ({
            ...line,
            price: line.price > 0 ? 1 / line.price : line.price,
          }))
        : chartLines,
    [chartLines, shouldInvertChartLines]
  );

  if (!chartTokenSymbol) {
    return null;
  }

  return (
    <div className="relative grow">
      <TVChartContainer
        chartLines={displayedChartLines}
        chainId={chainId}
        showDynamicChartLines
        period={period}
        setIsCandlesLoaded={setIsCandlesLoaded}
        visualMultiplier={visualMultiplier}
        setPeriod={setPeriod}
        chartToken={chartTokenProp}
        marketAddress={marketAddress}
        supportedResolutions={SUPPORTED_RESOLUTIONS_V2}
      />
    </div>
  );
}
